import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Card,
  CardMedia,
  IconButton,
  Skeleton,
  Alert,
  Fade,
} from "@mui/material";
import {
  Close,
  Person,
  Work,
  Favorite,
  LocationOn,
  Place,
  Edit,
} from "@mui/icons-material";
import { UserProfile, PreferredAge } from "../types/profile";
import { getUserProfile } from "../api/profileApi";
import EditProfileModal from "./EditProfileModal";

type Props = {
  open: boolean;
  onClose: () => void;
};

// 선호 연령 한국어 매핑

const preferredAgeLabels = {
  [PreferredAge.YOUNGER]: "연하",
  [PreferredAge.OLDER]: "연상", 
  [PreferredAge.ANY]: "상관없음",
};

export default function MyProfileModal({ open, onClose }: Props) {
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);

  const loadProfile = React.useCallback(async () => {
    if (!open) return;
    
    setLoading(true);
    setError(null);
    try {
      const profileData = await getUserProfile();
      setProfile(profileData);
    } catch (err: any) {
      setError(err.message || "프로필을 불러올 수 없습니다.");
    } finally {
      setLoading(false);
    }
  }, [open]);

  React.useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // 모달이 닫혔을 때 에러 상태 초기화
  React.useEffect(() => {
    if (!open) {
      setError(null);
      setProfile(null);
      setLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    setProfile(null);
    setError(null);
  };

  const handleEditOpen = () => {
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  const handleEditSave = () => {
    // Refresh profile data after successful edit
    loadProfile();
  };

  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  };

  const age = calculateAge(profile?.birthDate);

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxHeight: '90vh',
          m: { xs: 1, sm: 2 },
          overflow: 'hidden',
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* 네이비 그라디언트 헤더 */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0D1F3C, #1A3360)',
            p: 3,
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: '2px solid #C9A86C',
                bgcolor: 'rgba(201,168,108,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Playfair Display', serif",
                fontSize: 20,
                fontWeight: 700,
                color: '#C9A86C',
                flexShrink: 0,
              }}
            >
              {'나'}
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontFamily: "'Playfair Display', serif",
                color: '#FFFFFF',
              }}
            >
              내 프로필
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            <Close />
          </IconButton>
        </Box>

        {/* 콘텐츠 */}
        <Box sx={{ px: 3, pb: 3 }}>
          {loading && (
            <Stack spacing={3}>
              <Skeleton variant="rounded" height={200} />
              <Skeleton variant="text" height={40} />
              <Skeleton variant="text" height={120} />
            </Stack>
          )}

          {error && open && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {profile && (
            <Stack spacing={3}>
              {/* 상태 표시 */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Chip 
                  icon={<Person />}
                  label="프로필 정보"
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {/* 프로필 사진들 */}
              {profile.profilePhotos.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    프로필 사진
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                    gap: 2 
                  }}>
                    {profile.profilePhotos
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((photo, index) => (
                      <Card key={photo.id} sx={{ 
                        borderRadius: 2,
                        position: 'relative',
                        '&:hover': { transform: 'scale(1.02)' },
                        transition: 'transform 0.2s ease-in-out'
                      }}>
                        <CardMedia
                          component="img"
                          height="120"
                          image={photo.endpointUrl}
                          alt={`프로필 사진 ${index + 1}`}
                          sx={{ objectFit: 'cover' }}
                        />
                        {index === 0 && (
                          <Chip
                            label="대표"
                            size="small"
                            color="primary"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}

              {/* 기본 정보 */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  기본 정보
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2 
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 2,
                      bgcolor: '#F0F3F8',
                      borderRadius: 2
                    }}>
                      <Person color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          나이
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {age ? `${age}세` : '미설정'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 2,
                      bgcolor: '#F0F3F8',
                      borderRadius: 2
                    }}>
                      <Work color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          직업
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {profile.jobTitle || '미설정'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* 거주지역 */}
                  {profile.residenceRegion && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 2,
                      bgcolor: '#F0F3F8',
                      borderRadius: 2
                    }}>
                      <LocationOn color="primary" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          거주지역
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {profile.residenceRegion.level1Name} {profile.residenceRegion.level2Name}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* 소개 */}
              {(profile.oneLiner || profile.selfIntro) && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    소개
                  </Typography>
                  <Stack spacing={2}>
                    {profile.oneLiner && (
                      <Box sx={{ p: 2, bgcolor: 'rgba(13,31,60,0.05)', borderRadius: 2 }}>
                        <Typography variant="caption" color="primary.700" sx={{ fontWeight: 600 }}>
                          한줄소개
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                          {profile.oneLiner}
                        </Typography>
                      </Box>
                    )}
                    {profile.selfIntro && (
                      <Box sx={{ p: 2, bgcolor: '#F0F3F8', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          자기소개
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                          {profile.selfIntro}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}

              {/* 관심사 */}
              {profile.interests.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    관심사
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {profile.interests.map((interest) => (
                      <Chip
                        key={interest.id}
                        label={interest.name}
                        sx={{ 
                          fontWeight: 600,
                          bgcolor: 'rgba(201,168,108,0.08)',
                          color: '#8A6A40',
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* 선호 조건 */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  선호 조건
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    bgcolor: '#F0F3F8',
                    borderRadius: 2
                  }}>
                    <Favorite color="secondary" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        선호 연령대
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {preferredAgeLabels[profile.preferredAge]}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* 선호지역 */}
                  {profile.preferredRegions.length > 0 && (
                    <Box sx={{ 
                      p: 2,
                      bgcolor: '#F0F3F8',
                      borderRadius: 2
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        mb: 1
                      }}>
                        <Place color="secondary" />
                        <Typography variant="caption" color="text.secondary">
                          선호지역
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {profile.preferredRegions.map((region, index) => (
                          <Chip
                            key={`${region.level1Id}-${region.level2Id}-${index}`}
                            label={`${region.level1Name} ${region.level2Name}`}
                            size="small"
                            sx={{ 
                              fontWeight: 600,
                              bgcolor: 'rgba(201,168,108,0.12)',
                              color: '#8A6A40',
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ borderRadius: 2, fontWeight: 700, flex: 1 }}
        >
          닫기
        </Button>
        <Button
          onClick={handleEditOpen}
          variant="contained"
          startIcon={<Edit />}
          sx={{ borderRadius: 2, fontWeight: 700, flex: 1 }}
        >
          수정
        </Button>
      </DialogActions>
    </Dialog>
    {profile && (
      <EditProfileModal
        open={editOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        profile={profile}
      />
    )}
    </>
  );
}